import React, { useContext, useEffect, useRef } from "react";

import UserProfileModal from "./modals/UserProfileModal";

import Footer from "./Footer";
import Modal from "./common/Modal";

import UserContext from "../context/UserContext";
import UIContext from "../context/UIContext";
import SocketContext from "../context/SocketContext";
import { useNavigate } from "react-router-dom";
import CookieConsent from "react-cookie-consent";
import CodeOfConductModal from './modals/CodeOfConductModal';

function ConstellationCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let nodes = [];
    const NODE_COUNT = 30;       // was 55 — fewer nodes = O(n²) win
    const MAX_DIST = 130;        // was 160 — shorter connections = fewer pairs drawn
    const FLASH_CHANCE = 0.002;  // was 0.003
    const TARGET_FPS = 20;
    const FRAME_INTERVAL = 1000 / TARGET_FPS;
    let lastFrameTime = 0;

    const resize = () => {
      const prevW = canvas.width || window.innerWidth;
      const prevH = canvas.height || window.innerHeight;
      const scaleX = window.innerWidth / prevW;
      const scaleY = window.innerHeight / prevH;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      // Rescale existing node positions so they stay spread across the new size
      nodes.forEach(n => {
        n.x *= scaleX;
        n.y *= scaleY;
      });
    };
    resize();
    window.addEventListener('resize', resize);

    // Initialise nodes with random positions and slow drift
    for (let i = 0; i < NODE_COUNT; i++) {
      nodes.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        flash: 0, // flash countdown
      });
    }

    // Active flashing lines stored as { i, j, alpha, decay }
    let flashLines = [];

    const draw = (timestamp) => {
      animId = requestAnimationFrame(draw);
      if (timestamp - lastFrameTime < FRAME_INTERVAL) return;
      lastFrameTime = timestamp;

      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      // Move nodes
      nodes.forEach(n => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0) { n.x = 0; n.vx *= -1; }
        if (n.x > W) { n.x = W; n.vx *= -1; }
        if (n.y < 0) { n.y = 0; n.vy *= -1; }
        if (n.y > H) { n.y = H; n.vy *= -1; }
      });

      // Draw base connection lines (dim)
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < MAX_DIST) {
            const baseAlpha = (1 - dist / MAX_DIST) * 0.12;
            ctx.strokeStyle = `rgba(255,255,255,${baseAlpha})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();

            // Randomly spawn a flash on this line
            if (Math.random() < FLASH_CHANCE) {
              flashLines.push({ i, j, alpha: 1, decay: 0.025 + Math.random() * 0.04 });
            }
          }
        }
      }

      // Draw and age flash lines
      flashLines = flashLines.filter(fl => fl.alpha > 0);
      flashLines.forEach(fl => {
        ctx.strokeStyle = `rgba(180,210,255,${fl.alpha * 0.85})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(nodes[fl.i].x, nodes[fl.i].y);
        ctx.lineTo(nodes[fl.j].x, nodes[fl.j].y);
        ctx.stroke();
        fl.alpha -= fl.decay;
      });

      // Draw nodes
      nodes.forEach(n => {
        ctx.beginPath();
        ctx.arc(n.x, n.y, 1.8, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.25)';
        ctx.fill();
      });

    };

    animId = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0, left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
}


function MainLayout({ children }) {
  const navigate = useNavigate();
  const {
    showUserProfileModal,
    setShowUserProfileModal,
    saveUserProfile,
    systemModal,
    closeSystemModal,
    inGameRoom,
    activeLeaveRoomHandler,
    exitToLobby,
    toasts = [],
    removeToast,
  } = useContext(UIContext);
  const { user, setUser } = useContext(UserContext);
  const socket = useContext(SocketContext);
  const [incomingInvite, setIncomingInvite] = React.useState(null);
  const [cocOpen, setCocOpen] = React.useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = React.useState(false);
  const [pendingPath, setPendingPath] = React.useState(null);
  const cocCallbackRef = React.useRef(null);

  // Global handler for showing Code of Conduct when other components request it
  React.useEffect(() => {
    const handler = (e) => {
      // event detail may include { callback: fn }
      cocCallbackRef.current = e?.detail?.callback || null;
      setCocOpen(true);
    };
    window.addEventListener('open-coc', handler);
    return () => window.removeEventListener('open-coc', handler);
  }, []);
  React.useEffect(() => {
    if (!socket) return;
    const handler = (offer) => setIncomingInvite(offer);
    socket.on("lobbyInvite", handler);
    return () => socket.off("lobbyInvite", handler);
  }, [socket]);

  // Helper to validate emoji (simple unicode test)
  function isValidEmoji(str) {
    // Basic: must be a non-empty string and not just whitespace
    if (!str || typeof str !== 'string' || !str.trim()) return false;
    // Emoji regex (covers most, not all, cases)
    return /\p{Emoji}/u.test(str);
  }

  const handleHeaderNavigation = (path) => {
    if (inGameRoom && typeof activeLeaveRoomHandler === "function") {
      setPendingPath(path);
      setShowLeaveConfirm(true);
      return;
    }

    navigate(path);
  };

  const performLeaveRoomNavigation = React.useCallback(() => {
    try {
      if (typeof activeLeaveRoomHandler === "function") {
        activeLeaveRoomHandler();
      } else if (typeof exitToLobby === "function") {
        exitToLobby();
      }
    } catch (e) {
      if (typeof exitToLobby === "function") {
        exitToLobby();
      }
    }

    if (socket && socket.connected) return;
    if (typeof exitToLobby === "function") {
      exitToLobby();
    }
  }, [activeLeaveRoomHandler, exitToLobby, socket]);

  const confirmLeaveAndNavigate = () => {
    setShowLeaveConfirm(false);

    performLeaveRoomNavigation();
    window.scrollTo({ top: 0, behavior: 'auto' });
    navigate(pendingPath || "/");
    setPendingPath(null);
  };

  const cancelLeaveAndNavigate = () => {
    setShowLeaveConfirm(false);
    setPendingPath(null);
  };

  return (
    <div className="game-bg pb-20">
      <ConstellationCanvas />
     <div className="min-h-screen flex flex-col items-center gap-3 p-2 pt-4" style={{ position: 'relative', zIndex: 1 }}> 
     {/* bg-gray-100 dark:bg-gray-800 */}
      {/* Header with title and user profile button */}
      <div className="w-full max-w-4xl mx-auto flex flex-col">
        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 rounded-xl shadow-2xl main-panel-shadow px-4 py-2 mb-2">
          <CookieConsent
            location="bottom"
            buttonText="I understand"
            //cookieName="myAwesomeCookieName2"
            style={{ background: "#2B373B" }}
            buttonStyle={{ color: "#26271aff"}}
            expires={150}
          >
            This website uses cookies to enhance the user experience.{" "}
          </CookieConsent>
          <div className="flex items-center gap-3">
            
                      {/* Logo */}
            {/* <div className="flex-shrink-0">
              <img
                src="/logo/logo.png"
                alt="Game for a Laugh logo"
                className="w-60 h-30 object-contain rounded"
                onError={(e) => {
                  try { e.target.style.display = 'none'; } catch (err) {}
                }}
              />
            </div> */}
            {/* Game title */}
            <button
              type="button"
              onClick={() => handleHeaderNavigation("/")}
              className="flex items-center gap-2 select-none rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-50 dark:focus-visible:ring-offset-gray-900"
              aria-label="Go to lobby"
            >
              <span className="gfal-badge">GFAL</span>
              <span className="gfal-fullname hidden sm:inline">
                <span className="gfal-initial">G</span><span className="gfal-rest">ame </span>
                <span className="gfal-minor">for a </span>
                <span className="gfal-initial">L</span><span className="gfal-rest">augh</span>
              </span>
            </button>
          </div>
          <div className="flex items-center gap-4">
            {/* Audio toggle: updates user.audioEnabled and persists via saveUserProfile when available */}
            <button
              className={`px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-base font-semibold text-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-600 ${
                user && user.audioEnabled === false
                  ? "text-red-600 dark:text-red-400"
                  : ""
              }`}
              title={
                user && user.audioEnabled ? "Disable audio" : "Enable audio"
              }
              onClick={() => {
                try {
                  const next = {
                    ...(user || {}),
                    audioEnabled: !(user && user.audioEnabled),
                  };
                  setUser && setUser(next);
                  if (typeof saveUserProfile === "function")
                    saveUserProfile(next);
                } catch (e) {}
              }}
            >
              <span aria-hidden>
                {user && user.audioEnabled ? (
                  // Inline speaker icon - inherits text color from parent
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 8a5 5 0 0 1 0 8m2.7-11a9 9 0 0 1 0 14M6 15H4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1h2l3.5-4.5A.8.8 0 0 1 11 5v14a.8.8 0 0 1-1.5.5z"
                    />
                  </svg>
                ) : (
                  // Inline muted icon: speaker with red cross
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 8a5 5 0 0 1 1.912 4.934m-1.377 2.602A5 5 0 0 1 15 16m2.7-11a9 9 0 0 1 2.362 11.086m-1.676 2.299A9 9 0 0 1 17.7 19M9.069 5.054L9.5 4.5A.8.8 0 0 1 11 5v2m0 4v8a.8.8 0 0 1-1.5.5L6 15H4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1h2l1.294-1.664M3 3l18 18"
                    />
                  </svg>
                )}
              </span>
            </button>

            {/* User profile button */}
            <button
              className="flex items-center gap-1 px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-base font-semibold border border-gray-300 dark:border-gray-600 dark:text-gray-100"
              title="Edit player profile"
              onClick={() => setShowUserProfileModal(true)}
            >
              <span
                style={{
                  backgroundColor: user.color || "#e57373",
                  borderRadius: "4px",
                  padding: "2px 6px",
                  display: "inline-block",
                }}
              >
                {isValidEmoji(user.icon) ? user.icon : "😀"}
              </span>
              <span className="hidden md:inline">{user.username}</span>
            </button>
          </div>
        </div>
      </div>
      <div className="w-full max-w-4xl mx-auto flex flex-col min-h-[80vh]">
        
        <div className="flex-1 bg-gray-100 dark:bg-gray-900 rounded-xl shadow-2xl main-panel-shadow flex flex-col  p-2 sm:p-6">
                
        {!socket.connected ? (
          <div className="mb-6 flex-1 bg-gray-100 dark:bg-gray-900 rounded-xl shadow-2xl main-panel-shadow flex flex-col sm:flex-row gap-4 sm:gap-8 p-2 sm:p-6">
            <div className="w-full flex flex-col items-center justify-center py-8">
              <div className="mt-3 text-center text-gray-600 dark:text-gray-300">
                <div className="font-semibold text-2xl md:text-3xl pulsate">
                  Connecting
                </div>
                <div className="text-xl md:text-xl">
                  Please wait a moment...
                </div>
              </div>
            </div>
          </div>
        ) : null}

          {/* Main content wrapper — children placed inside to allow controlling the right column size */}
          <div className="w-full main-layout-children">
            {socket.connected ? children : null}
          </div>
        </div>
      </div>

      {/* Scoped styles to prevent the right column from growing too large on medium+ screens. 
          On md+ we lay out direct children horizontally (if not already) and cap the last child width. 
          Adjust --ml-right-max to change the cap. */}
      <style>{`
        @media (min-width: 768px) {
          .main-layout-children { display: flex; gap: 1rem; align-items: flex-start; }
           .main-layout-children > * { flex: 1 1 0%; min-width: 0; }
           /* Only cap the right-most child when there are multiple children.
             This prevents single-child pages (like Terms/Privacy) from being
             artificially constrained to a narrow sidebar width. */
           .main-layout-children > *:last-child:not(:only-child) { flex: 0 0 auto; max-width: 20rem; width: 100%; }
        }
        /* ── GFAL logo ───────────────────────────────────────────── */
        .gfal-badge {
          background: linear-gradient(135deg, #7c3aed 0%, #2563eb 100%);
          color: #fff;
          font-weight: 900;
          font-size: 1.1rem;
          letter-spacing: 0.14em;
          padding: 4px 11px;
          border-radius: 9px;
          box-shadow: 0 2px 10px rgba(124,58,237,0.5), 0 1px 3px rgba(0,0,0,0.2);
          display: inline-block;
          line-height: 1.4;
        }
        .gfal-fullname {
          font-size: 1.05rem;
          font-weight: 700;
          letter-spacing: -0.01em;
          line-height: 1;
        }
        .gfal-initial {
          color: #7c3aed;
          font-weight: 800;
          font-size: 1.1rem;
        }
        html.dark .gfal-initial { color: #a78bfa; }
        .gfal-rest {
          color: #1e40af;
          font-weight: 700;
        }
        html.dark .gfal-rest { color: #93c5fd; }
        .gfal-minor {
          color: #64748b;
          font-weight: 500;
          font-size: 0.88rem;
        }
        html.dark .gfal-minor { color: #94a3b8; }

        /* Main panel lift shadow */
        .main-panel-shadow {
          box-shadow: 0 10px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10);
        }
        html.dark .main-panel-shadow {
          box-shadow: 0 10px 40px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.35);
        }

        /* Decorative background for wide screens */
        .game-bg {
          background-color: #4f6fa0;
          background-image:
            radial-gradient(ellipse at top left, rgba(139,92,246,0.55) 0%, transparent 45%),
            radial-gradient(ellipse at bottom right, rgba(16,185,129,0.4) 0%, transparent 45%),
            radial-gradient(circle, rgba(255,255,255,0.18) 1.5px, transparent 1.5px);
          background-size: auto, auto, 22px 22px;
        }
        html.dark .game-bg {
          background-color: #1e293b;
          background-image:
            radial-gradient(ellipse at top left, rgba(139,92,246,0.25) 0%, transparent 45%),
            radial-gradient(ellipse at bottom right, rgba(16,185,129,0.2) 0%, transparent 45%),
            radial-gradient(circle, rgba(255,255,255,0.12) 1.5px, transparent 1.5px);
          background-size: auto, auto, 22px 22px;
        }

  /* Overlay removed; loader is inline and centered in the main content area */

        /* Pulsate animation for the startup message */
        .pulsate { display: inline-block; }
        @keyframes pulsateAnim { 0% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.06); opacity: 0.92; } 100% { transform: scale(1); opacity: 1; } }
        .pulsate { animation: pulsateAnim 1.6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .pulsate { animation: none; }
        }
      `}</style>

      {/* Footer */}
      <Footer />

      {toasts.length > 0 && (
        <div
          className="fixed left-1/2 -translate-x-1/2 flex flex-col gap-2 w-full max-w-md px-3"
          style={{ bottom: "5.5rem", zIndex: 20000, pointerEvents: "none" }}
          aria-live="polite"
          aria-atomic="true"
        >
          {toasts.map((toast) => (
            <button
              key={toast.id}
              type="button"
              onClick={() => typeof removeToast === "function" && removeToast(toast.id)}
              className="w-full rounded-2xl border border-amber-300/90 dark:border-amber-500/70 bg-gradient-to-r from-amber-50 via-white to-sky-50 dark:from-slate-800 dark:via-slate-900 dark:to-sky-950 text-left shadow-[0_18px_50px_rgba(15,23,42,0.42)] px-4 py-3 text-sm text-gray-900 dark:text-gray-100 backdrop-blur hover:brightness-[1.03]"
              style={{ pointerEvents: "auto" }}
              title="Click to dismiss"
            >
              <div className="flex items-start gap-3">
                <span
                  className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/70 dark:border-slate-700/80 text-base shadow-sm"
                  style={{ backgroundColor: toast.color || "#dbeafe", color: "#111827" }}
                >
                  {toast.icon || "💬"}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className="block text-xs font-bold uppercase tracking-[0.12em] text-slate-600 dark:text-slate-300"
                    style={{ color: toast.color || undefined }}
                  >
                    {toast.username || "New message"}
                  </span>
                  <span className="mt-0.5 block break-words text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {toast.message}
                  </span>
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      <CodeOfConductModal show={cocOpen} onClose={()=>{ setCocOpen(false); cocCallbackRef.current = null; }} onAccept={() => {
        try { if(typeof cocCallbackRef.current === 'function') cocCallbackRef.current(); } catch(e){}
        cocCallbackRef.current = null;
        setCocOpen(false);
      }} />

      {/* Advert pane — styled like the main content card and placed above the footer */}
      {/* <Advert /> */}

      
    </div>
  

      {showUserProfileModal && (
        <Modal
          show={showUserProfileModal}
          title="Edit Profile"
          onClose={() => setShowUserProfileModal(false)}
        >
          <UserProfileModal
            user={user}
            setUser={setUser}
            closeUserProfile={() => setShowUserProfileModal(false)}
            saveUserProfile={saveUserProfile}
          />
        </Modal>
      )}

      {systemModal && (
        <Modal
          show={true}
          title={systemModal.title}
          onClose={() => closeSystemModal()}
        >
          <div className="w-full max-w-md mx-auto p-4">
            <div className="mb-4">{systemModal.message}</div>
            {systemModal.reclaimDeadline && (
              <div className="mb-2 text-sm text-gray-600 dark:text-gray-300">
                Host reclaim in: {Math.max(0, Math.ceil((systemModal.reclaimDeadline - Date.now()) / 1000))}s
              </div>
            )}
            <div className="flex justify-end">
              <button
                className="px-3 py-1 rounded bg-blue-600 text-white"
                onClick={() => closeSystemModal()}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}



      {incomingInvite && (
        <Modal
          show={!!incomingInvite}
          title="Lobby Invite"
          onClose={() => setIncomingInvite(null)}
        >
          <div className="w-full max-w-md mx-auto p-4  dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded">
            <div className="mb-2">
              <strong>{incomingInvite.from?.username || "Someone"}</strong>{" "}
              invited you to join their room{" "}
              <em className="font-mono">
                {incomingInvite.roomName || incomingInvite.roomId}
              </em>
              .
            </div>
            {incomingInvite.message && (
              <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                {incomingInvite.message}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <a
                href={incomingInvite.link || "#"}
                className="px-3 py-1 rounded bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white"
              >
                Join
              </a>
              <button
                className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700"
                onClick={() => setIncomingInvite(null)}
              >
                Reject
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showLeaveConfirm && (
        <Modal show={showLeaveConfirm} title="Leave Game" onClose={cancelLeaveAndNavigate}>
          <div className="w-full max-w-md mx-auto p-4 bg-white dark:bg-gray-800 text-center">
            <p className="mb-4 text-lg dark:text-gray-100">
              Are you sure you want to leave the game room?
            </p>
            <div className="flex justify-center mt-4">
              <button
                className="bg-red-500 text-white p-2 rounded mr-2"
                onClick={confirmLeaveAndNavigate}
              >
                Yes, Leave
              </button>
              <button
                className="bg-gray-300 p-2 rounded dark:bg-gray-600 dark:text-gray-100"
                onClick={cancelLeaveAndNavigate}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default MainLayout;
